/**
 * Paginated Think GET /get-messages. Official Think returns `this.messages`
 * with no limit; a 500+ history (~2.7MB) parses on the main thread and
 * creates 14s+ longtasks. The agent slices on the server (limit + before).
 * Raw fetch must use kebab-case `project-agent` (`THINK_AGENT_URL_SLUG`);
 * `routeAgentRequest` does not match Pascal `ProjectAgent`.
 * https://developers.cloudflare.com/agents/communication-channels/chat/chat-agents/
 * https://developers.cloudflare.com/agents/runtime/communication/routing/
 */

import { THINK_AGENT_URL_SLUG } from "./think-agent.ts";

export const THINK_GET_MESSAGES_DEFAULT_LIMIT = 16;
export const THINK_GET_MESSAGES_HAS_MORE_HEADER = "x-think-messages-has-more";
export const THINK_GET_MESSAGES_CURSOR_HEADER = "x-think-messages-cursor";
export const THINK_GET_MESSAGES_TOTAL_HEADER = "x-think-messages-total";

export type ThinkHistoryMessage = {
  id?: string;
  role?: string;
  parts?: unknown;
  [key: string]: unknown;
};

export type ThinkMessagesPage = {
  messages: ThinkHistoryMessage[];
  hasMore: boolean;
  total: number;
  nextCursor: string | null;
};

export function paginateThinkMessages(
  messages: readonly ThinkHistoryMessage[] | null | undefined,
  opts: { limit?: number; before?: string | null } = {},
): ThinkMessagesPage {
  const all = Array.isArray(messages) ? [...messages] : [];
  let end = all.length;
  const before = typeof opts.before === "string" ? opts.before.trim() : "";
  if (before) {
    const idx = all.findIndex((row) => row && row.id === before);
    if (idx >= 0) end = idx;
  }
  const rawLimit = opts.limit;
  const limit =
    rawLimit == null || rawLimit < 0
      ? THINK_GET_MESSAGES_DEFAULT_LIMIT
      : rawLimit === 0
        ? all.length
        : rawLimit;
  const start = Math.max(0, end - limit);
  const page = all.slice(start, end);
  return {
    messages: page,
    hasMore: start > 0,
    total: all.length,
    nextCursor: page[0] && typeof page[0].id === "string" ? page[0].id : null,
  };
}

export function thinkGetMessagesSearch(opts: { limit?: number; before?: string | null } = {}): string {
  const params = new URLSearchParams();
  params.set("limit", String(opts.limit ?? THINK_GET_MESSAGES_DEFAULT_LIMIT));
  if (opts.before) params.set("before", opts.before);
  return `?${params.toString()}`;
}

export function thinkGetMessagesUrl(
  name: string,
  opts: { host?: string; limit?: number; before?: string | null } = {},
): string {
  const path = `/agents/${THINK_AGENT_URL_SLUG}/${name}/get-messages${thinkGetMessagesSearch(opts)}`;
  const host = opts.host?.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  if (!host) return path;
  const scheme = host.startsWith("127.") || host.startsWith("localhost") ? "http" : "https";
  return `${scheme}://${host}${path}`;
}

export async function fetchThinkMessagesPage(opts: {
  name: string;
  host?: string;
  limit?: number;
  before?: string | null;
  method?: "GET" | "HEAD";
  fetch?: typeof fetch;
}): Promise<ThinkMessagesPage & { hasMoreHeader: string | null }> {
  const url = thinkGetMessagesUrl(opts.name, opts);
  const fetchFn = opts.fetch ?? (typeof fetch === "function" ? fetch : undefined);
  if (!fetchFn) return { messages: [], hasMore: false, total: 0, nextCursor: null, hasMoreHeader: null };
  const res = await fetchFn(url, { credentials: "include", method: opts.method ?? "GET" });
  if (!res.ok) return { messages: [], hasMore: false, total: 0, nextCursor: null, hasMoreHeader: null };
  const headerMore = res.headers.get(THINK_GET_MESSAGES_HAS_MORE_HEADER);
  const headerCursor = res.headers.get(THINK_GET_MESSAGES_CURSOR_HEADER);
  let messages: ThinkHistoryMessage[] = [];
  if (opts.method !== "HEAD") {
    const body = (await res.json()) as unknown;
    messages = Array.isArray(body) ? (body as ThinkHistoryMessage[]) : [];
  }
  return {
    messages,
    hasMore: headerMore === "1" || headerMore === "true",
    hasMoreHeader: headerMore,
    total: Number(res.headers.get(THINK_GET_MESSAGES_TOTAL_HEADER) ?? messages.length) || messages.length,
    nextCursor: headerCursor || (messages[0] && typeof messages[0].id === "string" ? messages[0].id : null),
  };
}
